import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Image } from "expo-image";
import { TouchableOpacity } from "react-native";
import { Pill } from "./Pill";
import { Text } from "./Text";
import { View } from "./View";
import { useI18n } from "../lib/i18n";
import { Shadow, useTheme } from "../styles";
/**
 * Renders a button representing a specific artifact/activity/widget. Accepts
 * the following props:
 *
 * ```typescript
 * type Props = {
 *   &#47;**
 *    * A string if referencing a hosted GCS asset, otherwise an Svg illustration component.
 *    *&#47;
 *   assetName: string | FunctionComponent<SvgProps>;
 *   backgroundColor?: string;
 *   bannerText?: string;
 *   title: string;
 *   testID: string;
 *   shadow?: boolean;
 * } & (
 *   | {
 *       &#47;**
 *        * If locked, the button is not pressable
 *        *&#47;
 *       locked: true;
 *       badgeText?: string;
 *       onPress?: () => void;
 *     }
 *   | {
 *       locked: false;
 *       badgeText: string;
 *       &#47;**
 *        * Called when the button is pressed
 *        *&#47;
 *       onPress: () => void;
 *     }
 * );
 * ```
 *
 * @see {@link https://ouihealth.atlassian.net/browse/APPCORE-26}
 */ export function ArtifactButton(param) {
    var assetName = param.assetName, backgroundColor = param.backgroundColor, bannerText = param.bannerText, badgeText = param.badgeText, locked = param.locked, onPress = param.onPress, title = param.title, testID = param.testID, _param_shadow = param.shadow, shadow = _param_shadow === void 0 ? true : _param_shadow;
    var $t = useI18n().$t;
    var _useTheme = useTheme(), theme = _useTheme.theme, scheme = _useTheme.scheme;
    var unlockedBackgroundColor = backgroundColor !== null && backgroundColor !== void 0 ? backgroundColor : scheme === 'dark' ? 'white' : '#ebf1ed';
    return /*#__PURE__*/ _jsx(TouchableOpacity, {
        role: "button",
        testID: testID,
        onPress: onPress,
        disabled: locked,
        "aria-disabled": !!locked,
        style: [
            {
                borderRadius: 20,
                backgroundColor: locked ? theme.color.gray800 : unlockedBackgroundColor
            },
            locked || !shadow ? null : Shadow.high
        ],
        children: /*#__PURE__*/ _jsxs(View, {
            childFlex: 1,
            row: true,
            style: {
                justifyContent: 'space-between'
            },
            children: [
                /*#__PURE__*/ _jsxs(View, {
                    style: {
                        alignItems: 'flex-start',
                        alignSelf: 'flex-end',
                        borderBottomStartRadius: 20,
                        overflow: 'hidden'
                    },
                    children: [
                        /*#__PURE__*/ _jsx(View, {
                            style: {
                                borderBottomStartRadius: 20,
                                borderTopStartRadius: 20,
                                opacity: locked ? 0.1 : undefined,
                                overflow: 'hidden',
                                width: '100%'
                            },
                            children: /*#__PURE__*/ _jsx(Image, {
                                source: typeof assetName === 'string' && (assetName.startsWith('https://') || assetName.endsWith('svg')) ? assetName : "https://storage.googleapis.com/asset.oui.dev/static/artifacts/".concat(assetName, ".svg"),
                                style: {
                                    height: 100
                                },
                                contentFit: "contain",
                                contentPosition: "left center"
                            })
                        }),
                        bannerText ? /*#__PURE__*/ _jsx(View, {
                            style: {
                                backgroundColor: 'white',
                                bottom: 10,
                                left: 0,
                                padding: 5,
                                position: 'absolute',
                                right: 0,
                                alignItems: 'center'
                            },
                            children: /*#__PURE__*/ _jsx(Text, {
                                text: bannerText,
                                size: 13,
                                style: {
                                    textAlign: 'center'
                                }
                            })
                        }) : null
                    ]
                }),
                /*#__PURE__*/ _jsxs(View, {
                    spacing: 8,
                    style: {
                        paddingHorizontal: 20,
                        paddingVertical: 16,
                        alignItems: 'center',
                        height: 100
                    },
                    children: [
                        /*#__PURE__*/ _jsx(Text, {
                            text: title,
                            size: 17,
                            lineHeight: 22,
                            weight: "semibold",
                            style: {
                                textAlign: 'center'
                            },
                            color: locked ? theme.color.gray400 : undefined
                        }),
                        locked ? /*#__PURE__*/ _jsx(Pill, {
                            icon: "lock",
                            text: $t({
                                id: 'ArtifactButton_locked',
                                defaultMessage: 'Locked'
                            }),
                            color: theme.color.gray400
                        }) : /*#__PURE__*/ _jsx(Pill, {
                            text: badgeText
                        })
                    ]
                })
            ]
        })
    });
}
